import * as React from "react";
import type { AppointmentWithContext } from "@taskiq/shared";
import { formatDay, toValue, todayIn, type CalDate } from "@/lib/dates";
import {
  buildColumns,
  HOUR_MARKS,
  MINUTES_IN_DAY,
  pointerFrom,
  type DayColumn,
  type OpenAppointment,
  type Point,
} from "@/lib/schedule";
import { cn } from "@/lib/cn";
import { EventChip } from "./EventChip";

/**
 * The day/week grid: a time axis down the left, one column per day.
 *
 * ── Twenty-four rows, scrolled to the working day ──────────────────────────
 *
 * Not a 07:00-19:00 window. A technician's day starts before dawn in winter and
 * a storm callout does not respect office hours, so an appointment outside a
 * fixed window would be invisible rather than merely off-screen. The grid holds
 * the whole day and scrolls to the morning on mount, which loses nothing.
 *
 * ── Positioning is percentages, not pixels ─────────────────────────────────
 *
 * `top` and `height` come out of `lib/schedule.ts` as percentages of the day, so
 * the row height is a single CSS variable and nothing in the maths knows what a
 * pixel is. These are the legitimate `style={{}}` case the UI conventions carve
 * out: a value computed per render that no class could name.
 */
export function WeekGrid({
  days,
  appointments,
  timezone,
  onOpen,
  onCreateAt,
}: {
  days: CalDate[];
  appointments: AppointmentWithContext[];
  timezone: string;
  onOpen: OpenAppointment;
  /** Clicking empty space starts a new appointment at that hour. Omitted when the reader cannot schedule. */
  onCreateAt?: (date: CalDate, hour: number, anchor: HTMLElement, point: Point | null) => void;
}) {
  const columns = React.useMemo(
    () => buildColumns(days, appointments, timezone),
    [days, appointments, timezone]
  );

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const today = toValue(todayIn(timezone));

  // Open on the hour that matters, a third of the way down. In a layout effect
  // so it never renders at 00:00 and jumps, which reads as a bug.
  //
  // **A third, not centred and not at the top.** A calendar is read forwards:
  // below the line is what is still to come, above it is what already happened.
  // Centring spends half the viewport on hours nobody can act on, and pinning to
  // the top hides the job that started twenty minutes ago and is still running.
  // Same fraction and same fallback order as the phone's `TimeGrid`.
  //
  // Re-run on the range rather than only on mount: switching between the day and
  // week views keeps the same element, and the scroll position it was left at is
  // not the one the new view should open on. It is a no-op on a screen tall
  // enough to show the whole day, where there is nothing to scroll.
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const minutes = focusMinutes(columns, timezone);
    el.scrollTop = Math.max(
      0,
      (minutes / MINUTES_IN_DAY) * el.scrollHeight - el.clientHeight * FOCUS_FRACTION
    );
  }, [columns, timezone]);

  return (
    <div className={cn("weekgrid", days.length === 1 && "weekgrid--single")}>
      <div className="weekgrid__scroll" ref={scrollRef}>
        <div className="weekgrid__sticky">
          <div className="weekgrid__head">
            <div className="weekgrid__axis-head" aria-hidden />
            {columns.map((column) => (
              <div
                key={column.key}
                className={cn("weekgrid__day", column.key === today && "weekgrid__day--today")}
              >
                <span className="weekgrid__dow">{weekdayLabel(column.date)}</span>
                <span className="weekgrid__date tnum">{formatDay(asDate(column.date))}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="weekgrid__body">
          <div className="weekgrid__axis">
            {HOUR_MARKS.map((hour) => (
              <div key={hour} className="weekgrid__hour-label">
                {/* Midnight is the column's own top edge; a label there collides
                    with the header rule and says nothing the grid does not. */}
                {hour === 0 ? "" : formatHour(hour)}
              </div>
            ))}
          </div>

          {columns.map((column) => (
            <div
              key={column.key}
              className={cn("weekgrid__col", column.key === today && "weekgrid__col--today")}
            >
              {HOUR_MARKS.map((hour) => (
                <div
                  key={hour}
                  className="weekgrid__slot"
                  // A button per hour would be 168 tab stops across a week, for
                  // an action with a perfectly good toolbar equivalent. The
                  // keyboard route to creating an appointment is the New button;
                  // this is a pointer affordance and is marked as such.
                  role={onCreateAt ? "button" : undefined}
                  tabIndex={-1}
                  aria-hidden={onCreateAt ? undefined : true}
                  onClick={
                    onCreateAt
                      ? (event) =>
                          onCreateAt(column.date, hour, event.currentTarget, pointerFrom(event))
                      : undefined
                  }
                />
              ))}

              {column.events.map((positioned) => (
                <div
                  key={positioned.appointment.id}
                  className="weekgrid__event"
                  style={{
                    top: `${positioned.top}%`,
                    height: `${positioned.height}%`,
                    left: `${positioned.left}%`,
                    width: `${positioned.width}%`,
                  }}
                >
                  <EventChip
                    appointment={positioned.appointment}
                    timezone={timezone}
                    onOpen={onOpen}
                  />
                </div>
              ))}

              {column.key === today && <NowLine timezone={timezone} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The current time, as a hairline across today's column.
 *
 * It ticks once a minute rather than on a frame clock: this is a line that moves
 * a fraction of a pixel per second, and a rAF loop for it would keep the tab
 * awake all day to animate something nobody can see move.
 */
function NowLine({ timezone }: { timezone: string }) {
  const [minutes, setMinutes] = React.useState(() => minutesNowIn(timezone));

  React.useEffect(() => {
    const id = window.setInterval(() => setMinutes(minutesNowIn(timezone)), 60_000);
    return () => window.clearInterval(id);
  }, [timezone]);

  return (
    <div
      className="weekgrid__now"
      style={{ top: `${(minutes / MINUTES_IN_DAY) * 100}%` }}
      aria-hidden
    />
  );
}

/** See the layout effect above. */
const FOCUS_FRACTION = 0.3;

/**
 * Now if the range holds today, otherwise the first appointment in it,
 * otherwise the start of a working day.
 *
 * The order matters: a week grid opened on Wednesday afternoon should show
 * Wednesday afternoon, not Monday's 06:00 start. Only when today is nowhere in
 * the range does the earliest booking become the best guess at what the reader
 * came to look at.
 */
function focusMinutes(columns: DayColumn[], tz: string): number {
  const today = toValue(todayIn(tz));
  if (columns.some((column) => column.key === today)) return minutesNowIn(tz);

  let earliest = MINUTES_IN_DAY;
  for (const column of columns) {
    for (const event of column.events) {
      const minutes = (event.top / 100) * MINUTES_IN_DAY;
      if (minutes < earliest) earliest = minutes;
    }
  }
  return earliest === MINUTES_IN_DAY ? 8 * 60 : earliest;
}

function minutesNowIn(tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  return get("hour") * 60 + get("minute");
}

function asDate(date: CalDate): Date {
  return new Date(date.y, date.m - 1, date.d);
}

function weekdayLabel(date: CalDate): string {
  return new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(asDate(date));
}

function formatHour(hour: number): string {
  // Formatted rather than printed, so a US reader gets "9 AM" and everyone else
  // gets "09". The date is arbitrary - only the hour is rendered.
  return new Intl.DateTimeFormat(undefined, { hour: "numeric" }).format(new Date(2026, 0, 1, hour));
}
